import { KYC_LABELS } from '../constants/constants';
import { MERCHANT_EXPORT_COLUMNS } from '../table/columnDefs';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { mapAuditFields, mapAuditExportFields } from '../../../lib/auditColumnDefs';
import { toApiEntityStatus } from '../../../utils/status';
import type {
  MerchantColumnFilterKey,
  MerchantColumnFilters,
  MerchantGridRow,
  MerchantListRequest,
  MerchantRow,
} from '../models/types';

export function mapMerchantRow(m: Record<string, unknown>): MerchantRow {
  const volume = Number(m.totalVolume ?? 0);
  const txns = Number(m.totalTransactions ?? 0);
  const mids = Number(m.totalMids ?? 0);
  const statusRaw = String(m.status ?? '');
  const kycRaw = String(m.kycStatus ?? m.kyc ?? '').toUpperCase();

  return {
    id: Number(m.id ?? m.merchantId ?? m.autoId ?? 0),
    name: String(m.name || 'Unknown'),
    email: String(m.contactEmail || m.email || '—'),
    country: String(m.country || '—'),
    countryFlag: m.countryFlag != null ? String(m.countryFlag).trim().toUpperCase() : undefined,
    countryId: m.countryId != null && Number(m.countryId) > 0 ? Number(m.countryId) : undefined,
    kyc: KYC_LABELS[kycRaw] || 'Pending',
    status: statusRaw
      ? statusRaw.charAt(0) + statusRaw.slice(1).toLowerCase()
      : 'Unknown',
    category: String(m.category || '—'),
    volume,
    txns,
    mids,
    joined: m.createdAt
      ? new Date(String(m.createdAt)).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
        })
      : '—',
    createdAt: m.createdAt ? String(m.createdAt) : undefined,
    website: String(m.website || '—'),
    industry: String(m.industry || '—'),
    storefrontUrls: Array.isArray(m.storefrontUrls)
      ? m.storefrontUrls.map((url) => String(url)).filter(Boolean)
      : [],
    ...mapAuditFields(m),
  };
}

const MERCHANT_COLUMN_FILTER_KEYS: Record<MerchantColumnFilterKey, true> = {
  name: true,
  email: true,
  country: true,
  status: true,
  website: true,
  industry: true,
  createdAt: true,
};

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds `buildMerchantsRequest` — so header-funnel filters and
 * the drawer both produce identical `request.filters` payloads.
 *
 * Text filters → `{ [colId]: [filterText] }` (drawer multi-select uses string[] too).
 * Date column (`agDateColumnFilter` on createdAt) → `createdAt: [from, to]` yyyy-MM-dd.
 */
export function mapAgGridFilterModelToMerchantFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<MerchantColumnFilters> {
  const columnFilters: Partial<MerchantColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as {
      filter?: unknown;
      dateFrom?: string;
      dateTo?: string;
    };

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in MERCHANT_COLUMN_FILTER_KEYS) {
      columnFilters[colId as MerchantColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

export function buildMerchantGridRows(
  merchants: MerchantRow[],
  accentColors: string[],
): MerchantGridRow[] {
  return merchants.map((m, idx) => ({
    ...m,
    accent: accentColors[idx % accentColors.length],
  }));
}

export function buildMerchantsRequest(options: {
  page?: number;
  size?: number;
  filterStatus: string;
  search: string;
  columnFilters: MerchantColumnFilters;
  export?: boolean;
}): MerchantListRequest {
  const request: MerchantListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  if (options.filterStatus !== 'All') {
    request.status = toApiEntityStatus(options.filterStatus);
  }

  const query = options.search.trim();
  if (query) request.search = query;

  const filters = Object.fromEntries(
    (
      Object.entries(options.columnFilters) as [
        MerchantColumnFilterKey,
        string[] | null,
      ][]
    )
      .filter(([, values]) => Boolean(values?.length))
      .map(([key, values]) => [key, values as string[]]),
  );

  if (Object.keys(filters).length > 0) request.filters = filters;
  if (options.export) request.export = true;

  return request;
}

export async function exportMerchantsExcel(rows: MerchantRow[]) {
  await exportRowsToExcel(
    rows.map((row) => ({
      name: row.name,
      email: row.email,
      country: row.country,
      mids: row.mids,
      website: row.website,
      // Full list here, unlike the grid's truncated "url1 +N" summary — an export is exactly
      // where you want every submitted URL, not just the first one.
      storefrontUrlsDisplay: row.storefrontUrls.length > 0 ? row.storefrontUrls.join(', ') : '—',
      industry: row.industry,
      ...mapAuditExportFields(row),
      status: row.status,
    })),
    MERCHANT_EXPORT_COLUMNS,
    {
      sheetName: 'Merchants',
      fileNamePrefix: 'merchants',
    },
  );
}
